type AuthFetch = (
  endpoint: string,
  tag: string,
  options?: RequestInit,
  timeout?: number,
  behavior?: { suppress401Toast?: boolean; suppressNetworkBanner?: boolean },
) => Promise<unknown>;

export interface FrontendErrorRecord {
  event_kind: "frontend_error";
  source:
    | "app_boundary"
    | "window_error"
    | "unhandled_rejection"
    | "chat_send"
    | "chat_history";
  error_name: string;
  message: string;
  component_stack: string;
  browser_name: string;
  browser_major: string;
  os: string;
  route: string;
  build_id: string;
  workspace_id?: string;
  client_ts: string;
  request_context?: FrontendRequestFailureContextRecord;
}

export type FrontendRequestFailureContext =
  | {
      requestId: string;
      kind: "chat_post" | "history_get" | "resume_get";
      phase: "fetch_rejected";
    }
  | {
      requestId: string;
      kind: "chat_post" | "history_get" | "resume_get";
      phase:
        | "http_response"
        | "response_read"
        | "stream_read"
        | "server_event";
      status: number;
    };

export interface FrontendRequestFailureContextRecord {
  request_id: string;
  kind: FrontendRequestFailureContext["kind"];
  phase: FrontendRequestFailureContext["phase"];
  status?: number;
}

const REQUEST_ID_PATTERN = /^[0-9a-f]{8,32}$/;
const REQUEST_KINDS = new Set(["chat_post", "history_get", "resume_get"]);
const REQUEST_PHASES = new Set([
  "fetch_rejected",
  "http_response",
  "response_read",
  "stream_read",
  "server_event",
]);

export function createFrontendRequestId(): string {
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    // This identifier is for correlation, not authentication or secrecy.
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function serializeFrontendRequestFailureContext(
  context: FrontendRequestFailureContext,
): FrontendRequestFailureContextRecord | undefined {
  if (
    !context ||
    !REQUEST_ID_PATTERN.test(context.requestId) ||
    !REQUEST_KINDS.has(context.kind) ||
    !REQUEST_PHASES.has(context.phase)
  ) {
    return undefined;
  }
  if (context.phase === "fetch_rejected") {
    return {
      request_id: context.requestId,
      kind: context.kind,
      phase: context.phase,
    };
  }
  if (!Number.isInteger(context.status) || context.status < 100 || context.status > 599) {
    return undefined;
  }
  if (
    (context.phase === "http_response" && context.status >= 200 && context.status < 300) ||
    (context.phase !== "http_response" &&
      (context.status < 200 || context.status >= 300))
  ) {
    return undefined;
  }
  return {
    request_id: context.requestId,
    kind: context.kind,
    phase: context.phase,
    status: context.status,
  };
}

export type StreamTroubleKind = "stream_dropped" | "stream_retries_exhausted";

export interface DocumentCardCompletionDiagnostic {
  jobId: string;
  toolExecutionId?: string;
  executionStatus: string;
  executionFound: boolean;
  storeState: string;
}

export interface FrontendStreamTroubleRecord {
  event_kind: StreamTroubleKind;
  attempt: number;
  elapsed_ms: number;
  last_event_type: string;
  browser_name: string;
  browser_major: string;
  os: string;
  route: string;
  build_id: string;
  workspace_id?: string;
  client_ts: string;
}

const MAX_RECORDS_PER_PAGE = 5;
const reportedFingerprints = new Set<string>();
let pageRecordCount = 0;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+(?:@|%40)[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const BEARER_PATTERN = /\bBearer\s+[^\s,;]+/gi;
const NAMED_SECRET_PATTERN =
  /\b(api[_-]?key|access[_-]?token|refresh[_-]?token|token|secret|authorization)\s*[:=]\s*[^\s,;]+/gi;
const LONG_TOKEN_PATTERN = /\b[A-Za-z0-9_+/=-]{20,}\b/g;
const LONG_QUOTED_PATTERN = /(["'])(.*?)\1/g;

function redactUrl(value: string): string {
  return value.replace(URL_PATTERN, (match) => {
    const trailing = match.match(/[.,;:!?)}\]]*$/)?.[0] ?? "";
    const candidate = trailing ? match.slice(0, -trailing.length) : match;
    try {
      const parsed = new URL(candidate);
      return `${parsed.origin}${parsed.pathname}${trailing}`;
    } catch {
      return `[redacted-url]${trailing}`;
    }
  });
}

export function redactDiagnosticText(value: string): string {
  return redactUrl(value)
    .replace(EMAIL_PATTERN, "[redacted-email]")
    .replace(BEARER_PATTERN, "Bearer [redacted]")
    .replace(
      NAMED_SECRET_PATTERN,
      (_match, name: string) => `${name}=[redacted]`,
    )
    .replace(LONG_QUOTED_PATTERN, (match, quote: string, contents: string) =>
      contents.length > 40 ? `${quote}[redacted]${quote}` : match,
    )
    .replace(LONG_TOKEN_PATTERN, "[redacted-token]");
}

export function getReducedBrowserInfo(userAgent = navigator.userAgent): {
  browserName: string;
  browserMajor: string;
  os: string;
} {
  const browserMatch =
    userAgent.match(/Edg\/(\d+)/) ??
    userAgent.match(/(?:Firefox|FxiOS)\/(\d+)/) ??
    userAgent.match(/(?:Chrome|CriOS)\/(\d+)/) ??
    userAgent.match(/Version\/(\d+).*Safari/);
  const browserName = userAgent.includes("Edg/")
    ? "Edge"
    : /(?:Firefox|FxiOS)\//.test(userAgent)
      ? "Firefox"
      : /(?:Chrome|CriOS)\//.test(userAgent)
        ? "Chrome"
        : /Version\/\d+.*Safari/.test(userAgent)
          ? "Safari"
          : "Other";
  const os = /Android/i.test(userAgent)
    ? "Android"
    : /iPhone|iPad|iPod/i.test(userAgent)
      ? "iOS"
      : /Windows NT/i.test(userAgent)
        ? "Windows"
        : /Mac OS X/i.test(userAgent)
          ? "macOS"
          : /Linux/i.test(userAgent)
            ? "Linux"
            : "Other";
  return {
    browserName,
    browserMajor: browserMatch?.[1] ?? "0",
    os,
  };
}

function getRoute(pathname: string): { route: string; workspaceId?: string } {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  const workspaceMatch = path.match(/^\/workspace\/([^/]+)/);
  const workspaceId = workspaceMatch?.[1];
  const route = path
    .replace(/^\/workspace\/[^/]+/, "/workspace/:id")
    .replace(EMAIL_PATTERN, ":id")
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
      ":id",
    )
    .replace(/(?<=\/)(?:\d+|[A-Za-z0-9_-]{24,})(?=\/|$)/g, ":id")
    .replace(/[^A-Za-z0-9_:/.-]/g, ":");
  return {
    route: route.slice(0, 300),
    workspaceId: workspaceId?.slice(0, 100),
  };
}

export function createFrontendErrorRecord(
  error: unknown,
  componentStack = "",
  source: FrontendErrorRecord["source"] = "window_error",
  pathname = window.location.pathname,
  clientDate = new Date(),
  requestContext?: FrontendRequestFailureContext,
): FrontendErrorRecord {
  const errorObject = error instanceof Error ? error : undefined;
  const rawMessage =
    errorObject?.message ??
    (typeof error === "string" ? error : "Unknown error");
  const message = redactDiagnosticText(rawMessage.split(/[\r\n]/, 1)[0]).slice(
    0,
    300,
  );
  const stack = componentStack
    .split("\n")
    .map((frame) => redactDiagnosticText(frame.trim()))
    .filter(Boolean)
    .slice(0, 10)
    .join("\n")
    .slice(0, 2000);
  const { route, workspaceId } = getRoute(pathname);
  const browser = getReducedBrowserInfo();
  const record: FrontendErrorRecord = {
    event_kind: "frontend_error",
    source,
    error_name: redactDiagnosticText(errorObject?.name || "Error").slice(
      0,
      100,
    ),
    message,
    component_stack: stack,
    browser_name: browser.browserName,
    browser_major: browser.browserMajor,
    os: browser.os,
    route,
    build_id:
      process.env.REACT_APP_BUILD_ID ||
      process.env.REACT_APP_GIT_SHA ||
      "development",
    client_ts: clientDate.toISOString(),
  };
  if (workspaceId) record.workspace_id = workspaceId;
  const serializedRequestContext = requestContext
    ? serializeFrontendRequestFailureContext(requestContext)
    : undefined;
  if (serializedRequestContext) {
    record.request_context = serializedRequestContext;
  }
  return record;
}

export function createFrontendStreamTroubleRecord(
  eventKind: StreamTroubleKind,
  details: {
    attempt: number;
    elapsedMs: number;
    lastEventType: string;
    workspaceId?: string;
  },
  pathname = window.location.pathname,
  clientDate = new Date(),
): FrontendStreamTroubleRecord {
  const { route, workspaceId: routeWorkspaceId } = getRoute(pathname);
  const browser = getReducedBrowserInfo();
  const record: FrontendStreamTroubleRecord = {
    event_kind: eventKind,
    attempt: Math.max(0, Math.floor(details.attempt)),
    elapsed_ms: Math.max(0, Math.floor(details.elapsedMs)),
    last_event_type: details.lastEventType.slice(0, 80),
    browser_name: browser.browserName,
    browser_major: browser.browserMajor,
    os: browser.os,
    route,
    build_id:
      process.env.REACT_APP_BUILD_ID ||
      process.env.REACT_APP_GIT_SHA ||
      "development",
    client_ts: clientDate.toISOString(),
  };
  const workspaceId = details.workspaceId ?? routeWorkspaceId;
  if (workspaceId) record.workspace_id = workspaceId.slice(0, 100);
  return record;
}

function postCappedRecord(
  authFetch: AuthFetch,
  record: FrontendErrorRecord | FrontendStreamTroubleRecord,
  fingerprint: string,
): void {
  if (
    pageRecordCount >= MAX_RECORDS_PER_PAGE ||
    reportedFingerprints.has(fingerprint)
  ) {
    return;
  }
  pageRecordCount += 1;
  reportedFingerprints.add(fingerprint);

  // Reporting is best effort and must not affect the failed page or its UI.
  void Promise.resolve()
    .then(() =>
      authFetch(
        "/api/frontend-errors",
        "report frontend diagnostic",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(record),
        },
        10000,
        { suppress401Toast: true, suppressNetworkBanner: true },
      ),
    )
    .catch(() => undefined);
}

export function createFrontendErrorReporter(authFetch: AuthFetch) {
  return (
    error: unknown,
    componentStack = "",
    source: FrontendErrorRecord["source"] = "window_error",
    requestContext?: FrontendRequestFailureContext,
  ): void => {
    const record = createFrontendErrorRecord(
      error,
      componentStack,
      source,
      window.location.pathname,
      new Date(),
      requestContext,
    );
    const fingerprint = [
      record.source,
      record.error_name,
      record.message,
      record.request_context?.request_id ?? "",
    ].join("\u0000");
    postCappedRecord(authFetch, record, fingerprint);
  };
}

export function reportPackageChatError(
  report: ReturnType<typeof createFrontendErrorReporter>,
  error: Error,
  context?: FrontendRequestFailureContext,
): void {
  const source = context?.kind === "history_get" ? "chat_history" : "chat_send";
  report(error, "", source, context);
}

export function createFrontendStreamTroubleReporter(authFetch: AuthFetch) {
  return (
    eventKind: StreamTroubleKind,
    details: {
      attempt: number;
      elapsedMs: number;
      lastEventType: string;
      workspaceId?: string;
    },
  ): void => {
    const record = createFrontendStreamTroubleRecord(eventKind, details);
    const fingerprint = [
      record.event_kind,
      record.attempt,
      record.last_event_type,
      record.route,
      record.workspace_id ?? "",
    ].join("\u0000");
    postCappedRecord(authFetch, record, fingerprint);
  };
}

/** Report only identifiers and fixed state names when a document card stalls. */
export function createDocumentCardCompletionReporter(authFetch: AuthFetch) {
  return (details: DocumentCardCompletionDiagnostic): void => {
    const safeId = (value: string) =>
      /^[A-Za-z0-9_-]{1,100}$/.test(value) ? value : "invalid";
    const message = [
      "doc_card_miss",
      `job_id=${safeId(details.jobId)}`,
      `found=${details.executionFound ? "true" : "false"}`,
      `exec_id=${details.toolExecutionId ? safeId(details.toolExecutionId) : "none"}`,
      `exec_status=${/^[a-z_]{1,30}$/.test(details.executionStatus) ? details.executionStatus : "unknown"}`,
      `store=${/^[a-z_]{1,30}$/.test(details.storeState) ? details.storeState : "unknown"}`,
    ].join(" ");
    const error = new Error(message);
    error.name = "DocCardPending";
    createFrontendErrorReporter(authFetch)(error, "", "chat_send");
  };
}

export function resetFrontendErrorReporterForTests(): void {
  reportedFingerprints.clear();
  pageRecordCount = 0;
}
